import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert, Box, Chip, LinearProgress, Paper, Stack, Tooltip, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { TableContainer, TableHead, TableRow } from "@mui/material";
import React from "react";
import { DEFAULT_JUDGE0_LANGUAGE, JUDGE0_LANGUAGES } from "../utils/judge0Languages";

// ─── Language list (Judge0 — shared with compiler, problems, course & skill test players) ─
export const LANGUAGES_LIST = JUDGE0_LANGUAGES;
export const DEFAULT_LANGUAGE = DEFAULT_JUDGE0_LANGUAGE;

// ─── Color palette ────────────────────────────────────────────────────────────
export const PALETTE = {
  border: "#eaf0fb",
  borderDark: "#dde8fb",
  bgLight: "#f7faff",
  bgHover: "#f0f6ff",
  primary: "#1976d2",
  textSecondary: "#6b7280",
  textMuted: "#9ca3af",
};

// ─── Difficulty chip colors ───────────────────────────────────────────────────
export const difficultyColor: Record<string, "success" | "warning" | "error" | "default"> = {
  beginner: "success",
  intermediate: "warning",
  advanced: "error",
};

export const statusColor: Record<string, { bg: string; color: string }> = {
  accepted:            { bg: "#f0fdf4", color: "#16a34a" },
  wrong_answer:        { bg: "#fef2f2", color: "#dc2626" },
  runtime_error:       { bg: "#fff7ed", color: "#ea580c" },
  compilation_error:   { bg: "#faf5ff", color: "#7c3aed" },
  time_limit_exceeded: { bg: "#fffbeb", color: "#d97706" },
  failed:              { bg: "#fef2f2", color: "#dc2626" },
};

// ─── Styled table primitives ──────────────────────────────────────────────────
export const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "12px 12px 0 0",
  border: `1px solid ${PALETTE.border}`,
  borderBottom: "none",
  boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
  maxHeight: "62vh",
  overflowY: "auto",
}));

export const StyledTableHead = styled(TableHead)(() => ({
  position: "sticky", top: 0, zIndex: 2,
  "& .MuiTableCell-root": {
    backgroundColor: PALETTE.bgLight, fontWeight: 700, fontSize: "13px",
    color: "#374151", borderBottom: `2px solid ${PALETTE.borderDark}`,
    whiteSpace: "nowrap", padding: "10px 14px",
  },
}));

export const StyledTableRow = styled(TableRow)(() => ({
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: PALETTE.bgHover },
  "&:last-child .MuiTableCell-root": { borderBottom: "none" },
  "& .MuiTableCell-root": {
    fontSize: "13px", color: "#374151",
    borderBottom: `1px solid ${PALETTE.border}`, padding: "10px 14px",
  },
}));

// ─── Filter bar ───────────────────────────────────────────────────────────────
export const FilterBarBox = styled(Box)(({ theme }) => ({
  display: "flex", flexWrap: "nowrap", alignItems: "center",
  gap: theme.spacing(1.5), padding: theme.spacing(1.75, 2),
  backgroundColor: PALETTE.bgLight, borderRadius: "12px",
  border: `1px solid ${PALETTE.borderDark}`, marginBottom: theme.spacing(1.5),
}));

export const filterSelectSx = {
  fontSize: "13px", bgcolor: "#fff", borderRadius: "8px",
  "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.primary },
};

// ─── SectionPaper ─────────────────────────────────────────────────────────────
export const SectionPaper = ({ children, sx = {} }: { children: React.ReactNode; sx?: object }) => (
  <Paper sx={{ borderRadius: "12px", border: `1px solid ${PALETTE.border}`, boxShadow: "0px 1px 4px rgba(0,0,0,0.06)", overflow: "hidden", ...sx }}>
    {children}
  </Paper>
);

// ─── SectionHeader ────────────────────────────────────────────────────────────
export const SectionHeader = ({ title, action }: { title: string; action?: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, borderBottom: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bgLight }}>
    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "primary.main" }}>{title}</Typography>
    {action}
  </Box>
);

// ─── MetricCard ───────────────────────────────────────────────────────────────
export const MetricCard = ({ label, value, icon, hint, color = "primary.main" }: {
  label: string; value: string | number; icon: React.ReactNode; hint?: string; color?: string;
}) => (
  <Paper sx={{ p: 2, borderRadius: "12px", border: `1px solid ${PALETTE.border}`, boxShadow: "0px 1px 4px rgba(0,0,0,0.06)", height: "100%" }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ color, display: "flex", p: 1, bgcolor: "#f0f4ff", borderRadius: "8px" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "11px", color: PALETTE.textSecondary, fontWeight: 500 }}>{label}</Typography>
        <Typography sx={{ fontSize: "22px", fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
        {hint && <Typography sx={{ fontSize: "11px", color: PALETTE.textMuted }}>{hint}</Typography>}
      </Box>
    </Stack>
  </Paper>
);

// ─── DifficultyChip ──────────────────────────────────────────────────────────
export const DifficultyChip = ({ difficulty }: { difficulty: string }) => (
  <Chip size="small" color={difficultyColor[difficulty] ?? "default"} label={difficulty}
    sx={{ fontSize: "11px", height: "22px", textTransform: "capitalize" }} />
);

// ─── StatusChip ───────────────────────────────────────────────────────────────
export const StatusChip = ({ status }: { status: string }) => {
  const c = statusColor[status] ?? { bg: "#f1f5f9", color: "#64748b" };
  return (
    <Chip size="small" label={String(status).replace(/_/g, " ")}
      sx={{ fontSize: "11px", height: "22px", bgcolor: c.bg, color: c.color,
        border: `1px solid ${c.color}20`, fontWeight: 600, textTransform: "capitalize" }} />
  );
};

// ─── BookmarkBtn ──────────────────────────────────────────────────────────────
export const BookmarkBtn = ({ bookmarked, onClick }: { bookmarked: boolean; onClick: () => void }) => (
  <Tooltip title={bookmarked ? "Remove bookmark" : "Bookmark"}>
    <Box component="span" onClick={(e) => { e.stopPropagation(); onClick(); }}
      sx={{ cursor: "pointer", color: bookmarked ? "primary.main" : PALETTE.textMuted, display: "flex", alignItems: "center" }}>
      {bookmarked ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
    </Box>
  </Tooltip>
);

// ─── ActivityCalendar ────────────────────────────────────────────────────────
export const ActivityCalendar = ({ data = [] }: { data?: any[] }) => {
  const byDate = new Map(data.map((item) => [item.date, item]));
  const days = Array.from({ length: 112 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (111 - i));
    const key = d.toISOString().slice(0, 10);
    const count = byDate.get(key)?.accepted || byDate.get(key)?.submissions || 0;
    return { key, count };
  });
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(28, 1fr)", gap: "3px" }}>
      {days.map((day) => (
        <Tooltip key={day.key} title={`${day.key}: ${day.count}`} arrow>
          <Box sx={{ aspectRatio: "1", borderRadius: "2px",
            bgcolor: day.count > 3 ? "#1b5e20" : day.count > 1 ? "#43a047" : day.count > 0 ? "#a5d6a7" : "#edf2f7" }} />
        </Tooltip>
      ))}
    </Box>
  );
};

// ─── ExecutionResultPanel ────────────────────────────────────────────────────
export const ExecutionResultPanel = ({ execution }: { execution: any }) => (
  <SectionPaper>
    <SectionHeader title="Execution Result" />
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <StatusChip status={execution.status} />
        <Typography sx={{ fontSize: "12px", color: PALETTE.textSecondary }}>
          {execution.passedTestCases}/{execution.totalTestCases} passed
        </Typography>
        <Typography sx={{ fontSize: "12px", color: PALETTE.textSecondary }}>{execution.runtimeMs ?? 0} ms</Typography>
        <Typography sx={{ fontSize: "12px", color: PALETTE.textSecondary }}>{execution.memoryKb ?? 0} KB</Typography>
      </Stack>
      {(execution.testCaseResults ?? []).map((r: any, i: number) => (
        <Alert key={i} severity={r.passed ? "success" : "error"}
          icon={r.passed ? <CheckCircleOutlineIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
          sx={{ mb: 0.75, py: 0.5, "& .MuiAlert-message": { width: "100%" } }}>
          <Typography sx={{ fontWeight: 700, fontSize: "12px" }}>{r.name}</Typography>
          {r.visible && r.output !== undefined && (
            <Typography component="pre" sx={{ fontSize: "11px", m: 0, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
              Output: {r.output || "(no output)"}
            </Typography>
          )}
          {(r.stderr || r.error || r.compile_output) && (
            <Typography component="pre" sx={{ fontSize: "11px", m: 0, whiteSpace: "pre-wrap", fontFamily: "monospace", color: "#b91c1c" }}>
              {r.stderr || r.error || r.compile_output}
            </Typography>
          )}
        </Alert>
      ))}
    </Box>
  </SectionPaper>
);

// ─── ProgressTrack ───────────────────────────────────────────────────────────
export const ProgressTrack = ({ label, value, max, color = "primary" }: {
  label: string; value: number; max: number; color?: "primary" | "success" | "warning" | "error";
}) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
      <Typography sx={{ fontSize: "12px", color: PALETTE.textSecondary }}>{label}</Typography>
      <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{value}/{max}</Typography>
    </Stack>
    <LinearProgress variant="determinate" value={max ? Math.round((value / max) * 100) : 0}
      color={color} sx={{ height: 6, borderRadius: 3 }} />
  </Box>
);

// ─── EmptyState ───────────────────────────────────────────────────────────────
export const EmptyState = ({ message }: { message: string }) => (
  <Box sx={{ textAlign: "center", py: 8 }}>
    <Typography sx={{ color: PALETTE.textSecondary, fontSize: "14px" }}>{message}</Typography>
  </Box>
);

// ─── PageHeader ───────────────────────────────────────────────────────────────
export const PageHeader = ({ title, action }: { title: string; action?: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "primary.main", px: 1 }}>{title}</Typography>
    {action}
  </Box>
);

// ─── LoadingBlock ─────────────────────────────────────────────────────────────
export const LoadingBlock = ({ message = "Loading..." }: { message?: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
    <Box sx={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid", borderColor: "primary.main", borderTopColor: "transparent", animation: "spin 0.8s linear infinite",
      "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />
    <Typography sx={{ color: PALETTE.textSecondary, fontSize: "14px" }}>{message}</Typography>
  </Box>
);
